import { Alert, Button, Modal } from '@payaca/ambrosia';
import * as customerActions from '@payaca/store/customer/customerActions';
import * as dealsActions from '@payaca/store/deals/dealsActions';
import * as jobContentActions from '@payaca/store/jobContent/jobContentActions';
import * as jobPaymentsActions from '@payaca/store/jobPayments/jobPaymentsActions';
import * as jobsActions from '@payaca/store/jobs/jobsActions';
import { Job } from '@payaca/types/jobTypesV2';
import {
  FunctionComponent,
  useCallback,
  useMemo,
  useState,
  type JSX,
  type ReactNode,
} from 'react';
import { useDispatch } from 'react-redux';
import { useIntercom } from 'react-use-intercom';

import { useEmailSuppressionResult } from '@/api/queries/emails/useGetEmailSuppressionResults';
import { useSelector } from '@/api/state';
import PreviewJobControl from '@/ui/components/previewJobControl/PreviewJobControl';
import {
  getJob,
  getJobContentByJobId,
  getJobPaymentsByJobId,
} from '@/utils/stateAccessors';

import { getJobContactFromCustomer } from '@payaca/helpers/customerHelper';
import { getJobType } from '@payaca/helpers/jobHelperV2';
import { useDeal } from '@payaca/store/hooks/appState';
import { useHistory } from 'react-router';
import { AddCustomerEmailModal, PreviewUpdateReminders, SendJob } from '..';
import VerifyAccountModal from '../verifyAccountModal/VerifyAccountModal';

type ModalConfig = {
  title: string;
  text: string | ReactNode;
  primaryText: string;
  primaryAction?: () => void;
  secondaryText?: string;
  secondaryAction?: () => void;
};

type Props = {
  jobId: number;
  sendJobToCustomer: (
    id: number,
    payload: {
      sendMeACopy: boolean;
      preButtonEmailText: string;
      postButtonEmailText: string;
    },
    cb: (err: any, response: any) => void
  ) => void;
};

const PreviewAndSendJobControl: FunctionComponent<Props> = ({
  jobId,
  sendJobToCustomer,
}: Props): JSX.Element => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { show } = useIntercom();

  const [modalConfig, setModalConfig] = useState<ModalConfig | null>(null);
  const [isSendJobDisabled, setIsSendJobDisabled] = useState(true);
  const [isSendingJob, setIsSendingJob] = useState(false);
  const [showAddCustomerEmailModal, setShowAddCustomerEmailModal] =
    useState(false);
  const [showVerifyAccountModal, setShowVerifyAccountModal] = useState(false);
  const [sendErrorMessages, setSendErrorMessages] = useState<string[]>([]);

  const job: Job | undefined = useSelector((state) => {
    return getJob(state, jobId);
  });

  const customer = useSelector((state) => {
    return state.customer.currentCustomer;
  });

  const customerContactEmail = useMemo(() => {
    if (!customer || !job) return;

    return getJobContactFromCustomer(customer, job.contactId)?.emailAddress;
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useMemo has missing dependencies: 'customer', 'job', and 'job.contactId'
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useMemo has unnecessary dependency: customer.contacts
  }, [customer?.contacts]);

  const emailSuppressionResult =
    useEmailSuppressionResult(customerContactEmail);

  const [sendJobPayload, setSendJobPayload] = useState<{
    sendMeACopy: boolean;
    preButtonEmailText: string;
    postButtonEmailText: string;
  }>({
    sendMeACopy: true,
    preButtonEmailText: '',
    postButtonEmailText: '',
  });

  const deal = useDeal(job?.dealId);

  const myProfile = useSelector((state: any) => state.users.myProfile);

  const jobContent = useSelector((state) => {
    return getJobContentByJobId(state, jobId);
  });

  const jobPayments = useSelector((state) =>
    getJobPaymentsByJobId(state, jobId)
  );

  const onJobUpdateSuccess = useCallback(() => {
    dispatch(jobsActions.requestGetJob(jobId));
    if (job) {
      dispatch(jobContentActions.requestGetJobContent(job.jobContentId));
    }
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useCallback has missing dependencies: 'job', and 'job.jobContentId'
  }, [dispatch, jobId]);

  const onDealPaymentsUpdateSuccess = useCallback(() => {
    if (job) {
      dispatch(dealsActions.requestGetDeal(job.dealId));
      dispatch(jobPaymentsActions.requestGetJobPaymentsForDeal(job.dealId));
    }
  }, [dispatch, job]);

  const jobType = useMemo(() => {
    if (!job) return;
    return getJobType(job);
  }, [job]);

  const handleErrorMessageOnSendAttempt = useCallback(
    (errorMessage: string) => {
      if (!job) return;
      if (errorMessage === 'DEMO_ENDED') {
        setModalConfig({
          title: 'Please upgrade',
          text: 'Your demo has ended, please choose a subscription to continue using Payaca',
          primaryText: 'View options',
          primaryAction: () => {
            history.push('/upgradeAccount');
          },
          secondaryText: 'Contact us',
          secondaryAction: () => {
            show();
          },
        });
      } else if (errorMessage === 'You have exceeded your subscription plan') {
        setModalConfig({
          title: 'Please upgrade',
          text: "You've reached the maximum number of Projects for your subscription, please upgrade your subscription to continue using Payaca",
          primaryText: 'Upgrade',
          primaryAction: () => {
            history.push('/upgradeAccount');
          },
          secondaryText: 'Contact us',
          secondaryAction: () => {
            show();
          },
        });
      } else if (errorMessage === 'EMAIL_INACTIVE') {
        const primaryContact = getJobContactFromCustomer(
          customer!, // FIXME
          job?.contactId || null
        );
        setModalConfig({
          title: 'Email address not active',
          text: `"${primaryContact?.emailAddress}" has previously rejected messages due to a bounce or spam complaint. Please edit your customer's email and try again.`,
          primaryText: 'OK',
        });
      } else if (errorMessage === 'XERO_EXPIRED') {
        setModalConfig({
          title: 'Xero connection expired',
          text: 'Your connection with Xero has expired, please head over to the Connections page to reconnect.',
          primaryText: 'Reconnect',
          primaryAction: () => {
            history.push('/connections');
          },
        });
      } else if (errorMessage === 'QB_DUPLICATE_REF') {
        setModalConfig({
          title: 'Quickbooks reference already exists',
          text: `Invoice reference ${
            job.customReference || job.reference
          } already exists in Quickbooks, please set a unique custom reference and try again.`,
          primaryText: 'OK',
        });
      } else if (errorMessage === 'QB_INCORRECT_REF_LENGTH') {
        setModalConfig({
          title: 'Quickbooks reference too long',
          text: 'Your invoice reference is too long for Quickbooks, please set a custom reference 21 characters or less and try again.',
          primaryText: 'OK',
        });
      } else {
        setModalConfig({
          title: 'Something went wrong',
          text:
            errorMessage ||
            'Please try again. Contact us if you continue to have problems.',
          primaryText: 'OK',
        });
      }
    },

    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useCallback has missing dependencies: 'show', 'job', 'job.contactId', 'job.reference', and 'job.customReference'
    [history, customer]
  );

  const navigateToDeal = useCallback(() => {
    history.push(`/projects/${deal?.publicId}`);
  }, [history, deal]);

  const sendJob = useCallback(() => {
    const payload = {
      sendMeACopy: sendJobPayload.sendMeACopy,
      preButtonEmailText: sendJobPayload.preButtonEmailText,
      postButtonEmailText: sendJobPayload.postButtonEmailText,
    };
    setIsSendingJob(true);
    sendJobToCustomer(jobId, payload, (error: any, response: any) => {
      setIsSendingJob(false);
      if (error) {
        if (error.message)
          handleErrorMessageOnSendAttempt(
            error.message.errors || error.message.error
          );
      } else {
        navigateToDeal();
      }
    });
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useCallback has unnecessary dependency: dispatch
  }, [
    jobId,
    sendJobPayload,
    dispatch,
    navigateToDeal,
    handleErrorMessageOnSendAttempt,
    sendJobToCustomer,
  ]);

  const sendJobCheck = useCallback(() => {
    if (!myProfile.verifiedAt) {
      setShowVerifyAccountModal(true);
    } else if (emailSuppressionResult?.isSuppressed) {
      setSendErrorMessages([
        `The Customer's email is blocked. Reason: ${emailSuppressionResult.reason}`,
      ]);
    } else {
      sendJob();
    }
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useCallback has missing dependencies: 'emailSuppressionResult.isSuppressed', and 'emailSuppressionResult.reason'
  }, [myProfile, sendJob]);

  return (
    <>
      {!!sendErrorMessages?.length && (
        <div className="mb-4">
          <Alert colour="red">
            <strong>This Proposal could not be sent:</strong>
            <ul>
              {sendErrorMessages.map((x, i) => (
                <li key={i}>{x}</li>
              ))}
            </ul>
          </Alert>
        </div>
      )}
      <PreviewJobControl
        jobId={jobId}
        includeDownloadPDFAction
        onJobUpdateSuccess={onJobUpdateSuccess}
        onDealPaymentsUpdateSuccess={onDealPaymentsUpdateSuccess}
        markAsSentCallback={navigateToDeal}
        primaryAction={{
          disabled: isSendJobDisabled,
          isProcessing: isSendingJob,
          onClick: () => !isSendingJob && sendJobCheck(),
          children: 'Send',
        }}
        prefixContent={
          <div className="mb-4 space-y-4">
            <div className="flex justify-end">
              <PreviewUpdateReminders />
            </div>
            <h2>Preview email</h2>
            {!!job && !!jobContent && (
              <SendJob
                job={job}
                jobContent={jobContent}
                customer={customer! /* FIXME */}
                jobPayments={jobPayments}
                preButtonEmailText={sendJobPayload.preButtonEmailText}
                postButtonEmailText={sendJobPayload.postButtonEmailText}
                sendMeACopy={sendJobPayload.sendMeACopy}
                onUpdate={(value?: { [key: string]: any }) => {
                  if (!value) return;
                  setSendJobPayload((sendJobPayload) => {
                    return {
                      ...sendJobPayload,
                      ...value,
                    };
                  });
                }}
                onSetIsSendJobDisabled={setIsSendJobDisabled}
              />
            )}
            <h2>Preview {jobType}</h2>
          </div>
        }
      />

      {job && (
        <AddCustomerEmailModal
          customer={customer}
          isOpen={showAddCustomerEmailModal}
          contactId={job?.contactId}
          onClose={() => setShowAddCustomerEmailModal(false)}
          customerUpdateCallback={() => {
            dispatch(
              customerActions.requestGetAndSetCurrentCustomer(
                customer!.id /* FIXME */
              )
            );
            setShowAddCustomerEmailModal(false);
          }}
        />
      )}
      <VerifyAccountModal
        isOpen={showVerifyAccountModal}
        onClose={() => setShowVerifyAccountModal(false)}
      />
      {modalConfig && (
        <Modal
          isOpen={true}
          title={modalConfig.title}
          onClose={() => setModalConfig(null)}
          size="sm"
        >
          <Modal.Body>
            {typeof modalConfig.text === 'string' ? (
              <p>{modalConfig.text}</p>
            ) : (
              modalConfig.text
            )}
          </Modal.Body>
          <Modal.Footer>
            <Modal.Footer.Actions>
              {modalConfig.secondaryText && (
                <Button
                  variant="outline"
                  onClick={() => {
                    modalConfig.secondaryAction?.();
                    setModalConfig(null);
                  }}
                >
                  {modalConfig.secondaryText}
                </Button>
              )}
              <Button
                onClick={() => {
                  modalConfig.primaryAction?.();
                  setModalConfig(null);
                }}
              >
                {modalConfig.primaryText}
              </Button>
            </Modal.Footer.Actions>
          </Modal.Footer>
        </Modal>
      )}
    </>
  );
};

export default PreviewAndSendJobControl;
